import React, { ReactElement, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material'
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material'
import JsonView from '@uiw/react-json-view'
import { useAppSelector } from 'src/store/hooks'
import { selectMotherSessionDetail } from '../mother.slice'
import NodeDetailModal from '../components/NodeDetailModal'

type GraphTabProps = {
  correlationID: string
}

let getNodeStatusIcon = (nodeID: string, graph: any) => {
  if (!graph?.Nodes) return null
  let node = graph.Nodes[nodeID]
  if (!node) return null

  if (graph.CompletedNodes?.includes(nodeID)) {
    return <CheckCircleIcon color="success" fontSize="small" />
  }
  if (graph.FailedNodes?.includes(nodeID)) {
    return <ErrorIcon color="error" fontSize="small" />
  }
  return <PendingIcon color="warning" fontSize="small" />
}

let getNodeStatusColor = (
  nodeID: string,
  graph: any,
): 'success' | 'error' | 'warning' | 'default' => {
  if (graph?.CompletedNodes?.includes(nodeID)) return 'success'
  if (graph?.FailedNodes?.includes(nodeID)) return 'error'
  return 'warning'
}

export default ({ correlationID }: GraphTabProps): ReactElement => {
  let sessionDetail = useAppSelector(selectMotherSessionDetail(correlationID))
  let [selectedNodeID, setSelectedNodeID] = useState<string | null>(null)

  let graph = (() => {
    try {
      return sessionDetail?.executionGraph
        ? JSON.parse(sessionDetail.executionGraph)
        : null
    } catch {
      return null
    }
  })()

  let handleCopyGraph = () => {
    if (graph) {
      navigator.clipboard.writeText(JSON.stringify(graph, null, 2))
    }
  }

  let handleNodeClick = (nodeID: string) => {
    setSelectedNodeID(nodeID)
  }

  if (!sessionDetail) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!graph) {
    return (
      <Box sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
        <Typography>No execution graph available</Typography>
      </Box>
    )
  }

  let nodeEntries = graph?.Nodes ? Object.entries(graph.Nodes) : []

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="h6">Execution Graph</Typography>
        <Button
          startIcon={<CopyIcon />}
          onClick={handleCopyGraph}
          size="small"
          variant="outlined"
          aria-label="Copy execution graph JSON to clipboard"
        >
          Copy JSON
        </Button>
      </Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        {/* Graph Summary */}
        <Box
          sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}
        >
          <Typography variant="subtitle1" gutterBottom>
            Graph Summary
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(120px, 1fr))',
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                id="total-nodes-label"
              >
                Total Nodes
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 'medium' }}
                aria-labelledby="total-nodes-label"
              >
                {Object.keys(graph.Nodes || {}).length}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                id="completed-nodes-label"
              >
                Completed
              </Typography>
              <Typography
                color="success.main"
                variant="body2"
                sx={{
                  fontWeight: 'medium',
                }}
                aria-labelledby="completed-nodes-label"
              >
                {graph.CompletedNodes?.length || 0}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                id="failed-nodes-label"
              >
                Failed
              </Typography>
              <Typography
                color="error.main"
                variant="body2"
                sx={{
                  fontWeight: 'medium',
                }}
                aria-labelledby="failed-nodes-label"
              >
                {graph.FailedNodes?.length || 0}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Objective
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 'medium', lineHeight: 1.5 }}
            >
              {graph.Objective || 'Not specified'}
            </Typography>
          </Box>
        </Box>

        {/* Nodes List */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Nodes ({nodeEntries.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Click a node to view detailed information
          </Typography>
          <List
            sx={{
              bgcolor: 'background.default',
              borderRadius: 1,
              maxHeight: 400,
              overflowY: 'auto',
            }}
          >
            {nodeEntries.map(([nodeID, node]: [string, any]) => (
              <React.Fragment key={nodeID}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleNodeClick(nodeID)}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                      }}
                    >
                      {getNodeStatusIcon(nodeID, graph)}
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {nodeID.slice(0, 16)}...
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            <Chip
                              label={getNodeStatusColor(nodeID, graph)}
                              size="small"
                              color={getNodeStatusColor(nodeID, graph)}
                              variant="outlined"
                            />
                            {node.Step?.tool && (
                              <Chip
                                label={node.Step.tool}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                      />
                    </Box>
                  </ListItemButton>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        </Box>

        {/* JSON Display */}
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Full Graph JSON
          </Typography>
          <Box
            sx={{
              bgcolor: 'background.default',
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              maxHeight: 500,
              overflowY: 'auto',
            }}
          >
            <JsonView
              value={graph}
              style={
                {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  fontSize: '14px',
                  '--w-rjv-color': '#ffffff',
                  '--w-rjv-key-string': '#9db7ff',
                  '--w-rjv-background-color': 'transparent',
                } as any
              }
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={true}
            />
          </Box>
        </Box>
      </Paper>
      {/* Node Detail Modal */}
      {selectedNodeID && (
        <NodeDetailModal
          open={!!selectedNodeID}
          onClose={() => setSelectedNodeID(null)}
          correlationID={correlationID}
          nodeID={selectedNodeID}
        />
      )}
    </Box>
  )
}
